import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { PrimaryButton } from "components/Buttons";
import { showErrorToast } from "components/NewToaster";
import SystemServices from "services/System";
import Colors from "assets/Style/Colors";

const HeaderCell = styled(TableCell)(() => ({
  fontWeight: 700,
  fontSize: 14,
  color: "#232323",
  border: "1px solid #d9d9d9",
  whiteSpace: "nowrap",
  background: "#f6f7f9",
  padding: "12px 10px",
}));

const BodyCell = styled(TableCell)(() => ({
  fontSize: 13,
  border: "1px solid #e7e7e7",
  padding: "10px",
  whiteSpace: "nowrap",
}));

const numberValue = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const COLUMNS = [
  { key: "customer_name", label: "Client", clickable: false },
  { key: "candidateCount", label: "Total Candidates", clickable: true },
  { key: "activeCount", label: "Active", clickable: true },
  { key: "expiredCount", label: "Expired", clickable: true },
  { key: "main_cancel_total", label: "Cancelled after Active", clickable: true },
  { key: "pending_total", label: "Pending", clickable: true },
  { key: "entry_permit_total", label: "Entry Permit", clickable: true },
  { key: "in_progress_total", label: "In Progress", clickable: true },
  { key: "change_status_total", label: "Change Status", clickable: true },
  { key: "medical_total", label: "Medical", clickable: true },
  { key: "emirate_id_total", label: "Emirates ID", clickable: true },
  { key: "cancel_total", label: "Cancelled in Process", clickable: true },
  { key: "reject_total", label: "Rejected in Process", clickable: true },
  { key: "absconder_total", label: "Absconder in Process", clickable: true },
  { key: "absconder_active_total", label: "Absconder After Active", clickable: true },
  { key: "absconder_removed_total", label: "Absconder Closed", clickable: true },
  {
    key: "rejectedCandidateCount",
    label: "Rejected Before Process (Excluded)",
    clickable: true,
    excluded: true,
  },
];

const EXCLUDED_COLUMN_KEY = "rejectedCandidateCount";
const EXPIRED_COLUMN_KEY = "expiredCount";

function OverallStats() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [overallTotal, setOverallTotal] = useState(null);

  const getStatValue = (source, key) => {
    if (!source) return 0;
    if (source[key] !== undefined && source[key] !== null) {
      return numberValue(source[key]);
    }
    return numberValue(source?.statuses?.[key]);
  };

  const navigateWithFilter = (route, state) => {
    navigate(route, { state });
  };

  const handleCellClick = (customerId, key) => {
    if (!customerId || !key) return;

    const processingStatusMap = {
      pending_total: "Pending",
      entry_permit_total: "Entry Permit",
      in_progress_total: "In Progress",
      change_status_total: "Change Status",
      medical_total: "Medical",
      emirate_id_total: "Emirates Id",
      cancel_total: "Cancel",
      reject_total: "Reject",
      absconder_total: "Absconder",
    };

    if (key === "candidateCount") {
      navigateWithFilter("/visa-processing-list", { filters: { customer_id: customerId } });
      localStorage.setItem("currentRoute", "Visa Processing Management");
      return;
    }

    if (key === "activeCount") {
      navigateWithFilter("/active-visa-list", { filters: { customer_id: customerId } });
      localStorage.setItem("currentRoute", "Active Visa List");
      return;
    }

    if (key === "expiredCount") {
      navigateWithFilter("/active-visa-list", {
        filters: { customer_id: customerId, expiry_status: "Expired" },
      });
      localStorage.setItem("currentRoute", "Active Visa List");
      return;
    }

    if (key === "main_cancel_total") {
      navigateWithFilter("/cancelled-visa-list", {
        filters: { customer_id: customerId, processing_status: "Canceled" },
      });
      localStorage.setItem("currentRoute", "Cancelled Visa List");
      return;
    }

    if (key === "rejectedCandidateCount") {
      navigateWithFilter("/rejected-visa-list", { filters: { customer_id: customerId } });
      localStorage.setItem("currentRoute", "Rejected Visa List");
      return;
    }

    if (key === "absconder_active_total") {
      navigateWithFilter("/absconder-visa-list", {
        filters: { customer_id: customerId, processing_status: "Absconder" },
      });
      localStorage.setItem("currentRoute", "Absconder Visa List");
      return;
    }

    if (key === "absconder_removed_total") {
      navigateWithFilter("/absconder-visa-list", {
        filters: { customer_id: customerId, processing_status: "Absconder Closed" },
      });
      localStorage.setItem("currentRoute", "Absconder Visa List");
      return;
    }

    if (processingStatusMap[key]) {
      navigateWithFilter("/visa-processing-list", {
        filters: {
          customer_id: customerId,
          last_status: processingStatusMap[key],
        },
        selectedStatus: {
          id: processingStatusMap[key],
          name: processingStatusMap[key],
        },
      });
      localStorage.setItem("currentRoute", "Visa Processing Management");
    }
  };

  const fetchOverallStats = async () => {
    setLoading(true);
    try {
      const { data } = await SystemServices.getOverallStatsReport();
      setRows(Array.isArray(data?.customers) ? data.customers : []);
      setOverallTotal(data?.overallTotal || null);
    } catch (error) {
      showErrorToast(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOverallStats();
  }, []);

  const exportExcel = () => {
    try {
      const headers = COLUMNS.map((col) => col.label);
      const reportRows = rows.map((item) =>
        COLUMNS.map((col) => {
          if (col.key === "customer_name") return item?.customer_name || "-";
          return getStatValue(item, col.key);
        })
      );

      const totalRow = COLUMNS.map((col, index) => {
        if (index === 0) return "Total";
        return getStatValue(overallTotal, col.key);
      });

      const ws = XLSX.utils.aoa_to_sheet([headers, ...reportRows, totalRow]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Comprehensive Report");

      const buf = XLSX.write(wb, {
        type: "array",
        bookType: "xlsx",
      });

      saveAs(
        new Blob([buf], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        `Comprehensive_Report_${new Date().toISOString().slice(0, 10)}.xlsx`
      );
    } catch (error) {
      showErrorToast(error);
    }
  };

  const totalCandidates = useMemo(
    () => rows.reduce((sum, item) => sum + numberValue(item?.candidateCount), 0),
    [rows]
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
          mb: 2,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 24, fontWeight: 800, color: "#1f2937" }}>
            Comprehensive Visa Stats
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#6b7280", mt: 0.5 }}>
            Click any number to open filtered candidates list.
          </Typography>
        </Box>

        <PrimaryButton
          title="Export Excel"
          style={{ backgroundColor: Colors.bluishCyan }}
          onClick={exportExcel}
          disabled={loading || rows.length === 0}
        />
      </Box>

      <Paper sx={{ border: "1px solid #d8d8d8" }}>
        <TableContainer sx={{ maxHeight: "calc(100vh - 230px)" }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {COLUMNS.map((column) => {
                  const isExcludedColumn = column.key === EXCLUDED_COLUMN_KEY;
                  const isExpiredColumn = column.key === EXPIRED_COLUMN_KEY;
                  return (
                    <HeaderCell
                      key={column.key}
                      sx={
                        isExcludedColumn
                          ? {
                              borderLeft: "3px solid #dc2626",
                              backgroundColor: "#fff1f2",
                              color: "#991b1b",
                            }
                          : isExpiredColumn
                          ? {
                              backgroundColor: "#fffbeb",
                              color: "#854d0e",
                              borderLeft: "2px solid #facc15",
                              borderRight: "2px solid #facc15",
                            }
                          : {}
                      }
                    >
                      {column.label}
                    </HeaderCell>
                  );
                })}
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <BodyCell colSpan={COLUMNS.length} sx={{ textAlign: "center", py: 5 }}>
                    <CircularProgress size={26} />
                  </BodyCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <BodyCell colSpan={COLUMNS.length} sx={{ textAlign: "center", py: 4 }}>
                    No records found
                  </BodyCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row?.customer_id || row?.customer_name} hover>
                    {COLUMNS.map((column) => {
                      const isClient = column.key === "customer_name";
                      const isExcludedColumn = column.key === EXCLUDED_COLUMN_KEY;
                      const isExpiredColumn = column.key === EXPIRED_COLUMN_KEY;
                      const value = isClient
                        ? row?.customer_name || "-"
                        : getStatValue(row, column.key);

                      const isClickable = column.clickable && !isClient;

                      return (
                        <BodyCell
                          key={`${row?.customer_id}-${column.key}`}
                          sx={
                            isExcludedColumn
                              ? {
                                  borderLeft: "3px solid #dc2626",
                                  backgroundColor: "#fff7f7",
                                }
                              : isExpiredColumn
                              ? {
                                  backgroundColor: "#fffbeb",
                                  borderLeft: "2px solid #fde68a",
                                  borderRight: "2px solid #fde68a",
                                }
                              : {}
                          }
                        >
                          {isClickable ? (
                            <Box
                              onClick={() => handleCellClick(row?.customer_id, column.key)}
                              sx={{
                                display: "inline-flex",
                                px: 0.5,
                                cursor: "pointer",
                                borderBottom: "1px dashed #64748b",
                                color: isExcludedColumn
                                  ? "#b91c1c"
                                  : isExpiredColumn
                                  ? "#a16207"
                                  : "#1e293b",
                                fontWeight: 700,
                              }}
                            >
                              {value}
                            </Box>
                          ) : (
                            value
                          )}
                        </BodyCell>
                      );
                    })}
                  </TableRow>
                ))
              )}

              {!loading && rows.length > 0 && (
                <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                  {COLUMNS.map((column, index) => {
                    const isExcludedColumn = column.key === EXCLUDED_COLUMN_KEY;
                    const isExpiredColumn = column.key === EXPIRED_COLUMN_KEY;
                    const value = index === 0 ? "Total" : getStatValue(overallTotal, column.key);
                    return (
                      <BodyCell
                        key={`total-${column.key}`}
                        sx={{
                          fontWeight: 800,
                          borderTop: "2px solid #cbd5e1",
                          ...(isExpiredColumn
                            ? {
                                backgroundColor: "#fef9c3",
                                color: "#854d0e",
                                borderLeft: "2px solid #facc15",
                                borderRight: "2px solid #facc15",
                              }
                            : {}),
                          ...(isExcludedColumn
                            ? {
                                borderLeft: "3px solid #dc2626",
                                backgroundColor: "#ffe4e6",
                                color: "#9f1239",
                              }
                            : {}),
                        }}
                      >
                        {value}
                      </BodyCell>
                    );
                  })}
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {!loading && rows.length > 0 && (
        <Typography sx={{ mt: 1.5, fontSize: 12, color: "#6b7280" }}>
          Total clients: {rows.length} | Total candidates: {totalCandidates} | Rejected Before Process is shown separately and excluded from row-wise processing totals.
        </Typography>
      )}
    </Box>
  );
}

export default OverallStats;
